import type { ComponentProps } from "react";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import NewIncidentIcon from "@mui/icons-material/Add";
import HomeIcon from "@mui/icons-material/Home";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import InventoryIcon from "@mui/icons-material/Inventory2Outlined";
import LogoutIcon from "@mui/icons-material/Logout";
import HelpIcon from "@mui/icons-material/Phone";
import SettingsIcon from "@mui/icons-material/Settings";
import Warning from "@mui/icons-material/Warning";
import { Avatar, Box, List, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import type { Prettify } from "@thalamos/common";
import { getNameInitials } from "@thalamos/common";
import {
  AppDrawerDivider,
  AppDrawerListItem,
  AppDrawer as ThalamosAppDrawer,
} from "@thalamos/frontend";
import type {
  AvailableLanguages,
  ExtendedVisionUser,
} from "@thalamos/vision-common";
import { isCurrentUserSupervisor, tk } from "@thalamos/vision-common";

import api from "../../api/index.js";
import { config } from "../../config/index.js";
import { createRoute } from "../../routing/createRoute.js";
import {
  BTPLogo,
  ColpLogo,
  MetLogo,
  MHFormsLogo,
  ThalamosLogo,
} from "../Logo/Logo.js";
import { TypographyI18N } from "../TypographyI18N/TypographyI18N.js";

export type AppDrawerProps = unknown;

const UserProfileAvatar = ({ user }: { user: ExtendedVisionUser }) => {
  const theme = useTheme();

  return (
    <Avatar
      sx={{
        transform: "scale(0.75)",
        bgcolor: theme.palette.primary.contrastText,
        color: theme.palette.primary.main,
      }}
    >
      {getNameInitials(user.name)}
    </Avatar>
  );
};

const UserProfileText = ({ user }: { user: ExtendedVisionUser }) => {
  return (
    <Box>
      <Typography>{user.name}</Typography>
      {user.shoulderNumber && <Typography>{user.shoulderNumber}</Typography>}
    </Box>
  );
};

const useApiVersion = () => {
  const [apiVersion, setApiVersion] = useState<string | undefined>(undefined);

  useEffect(() => {
    async function fetchVersion() {
      const result = await api.health.version();
      if (result.status === 200) {
        setApiVersion(result.data.version);
      } else {
        setApiVersion("unknown");
      }
    }
    void fetchVersion();
  }, []); // will run only once on mount

  return { apiVersion };
};

const capitalizeFirstLetter = (word: string): string => {
  if (!word) return "";
  return word.charAt(0).toUpperCase() + word.slice(1);
};

type DrawerItemsProps = Prettify<
  ReturnType<typeof useApiVersion> & {
    user: ExtendedVisionUser;
    hoverColor?: string;
    onHome: () => void;
    onIncidentCreate: () => void;
    onIncidentArchive: () => void;
    onHelp: () => void;
    onPolicyHelp: () => void;
    onUserProfile: () => void;
    onContextSelector: () => void;
    onLogout: () => void;
  }
>;

const DrawerItems = ({
  apiVersion,
  user,
  hoverColor,
  onHome,
  onIncidentCreate,
  onIncidentArchive,
  onHelp,
  onPolicyHelp,
  onUserProfile,
  onContextSelector,
  onLogout,
}: DrawerItemsProps) => {
  const { t } = useTranslation();
  const isAdminUser = user.isAdminSession;
  const isSupervisor = isCurrentUserSupervisor(user);
  const isOfficer = !isAdminUser && !isSupervisor;

  const isMetUser = user.sessionOrganisationConfiguration?.language === "enMet";
  const hasMultipleMemberships = user.memberships.length > 1;

  return (
    <Stack sx={{ display: "flex", flexGrow: 1, height: "100%" }}>
      <List>
        {(isAdminUser || isSupervisor) && (
          <AppDrawerListItem
            icon={<SettingsIcon />}
            text={<TypographyI18N translationKey={tk("navigation.admin")} />}
            onClick={onHome}
            hoverColor={hoverColor}
          />
        )}
        {isOfficer && (
          <AppDrawerListItem
            icon={<HomeIcon />}
            text={<TypographyI18N translationKey={tk("navigation.home")} />}
            onClick={onHome}
            hoverColor={hoverColor}
          />
        )}
        {isOfficer && (
          <AppDrawerListItem
            icon={<NewIncidentIcon />}
            text={
              <TypographyI18N translationKey={tk("navigation.newIncident")} />
            }
            onClick={onIncidentCreate}
            hoverColor={hoverColor}
          />
        )}
        {isOfficer && (
          <AppDrawerListItem
            icon={<InventoryIcon />}
            text={
              <TypographyI18N translationKey={tk("navigation.endedForms")} />
            }
            onClick={onIncidentArchive}
            hoverColor={hoverColor}
          />
        )}
        {(isOfficer || isSupervisor) && (
          <AppDrawerListItem
            icon={<HelpIcon />}
            text={<TypographyI18N translationKey={tk("navigation.help")} />}
            onClick={onHelp}
            hoverColor={hoverColor}
          />
        )}
        {isOfficer && isMetUser && (
          <AppDrawerListItem
            icon={<InfoOutlined />}
            text={
              <TypographyI18N translationKey={tk("navigation.policyHelp")} />
            }
            onClick={onPolicyHelp}
            hoverColor={hoverColor}
          />
        )}
      </List>
      <AppDrawerDivider />
      <Stack sx={{ marginTop: "auto" }}>
        <List>
          <AppDrawerListItem
            icon={<UserProfileAvatar user={user} />}
            text={<UserProfileText user={user} />}
            onClick={onUserProfile}
            hoverColor={hoverColor}
          />
          <AppDrawerListItem
            icon={<></>}
            text={
              <Box>
                <Typography>
                  {user.sessionContext?.role
                    ? capitalizeFirstLetter(user.sessionContext.role)
                    : ""}
                </Typography>
                {hasMultipleMemberships && (
                  <Typography sx={{ textDecoration: "underline white" }}>
                    {t("navigation.changeRole")}
                  </Typography>
                )}
              </Box>
            }
            onClick={hasMultipleMemberships ? onContextSelector : undefined}
            hoverColor={hasMultipleMemberships ? hoverColor : undefined}
          />
        </List>
        <AppDrawerDivider />
        <List>
          <AppDrawerListItem
            icon={<LogoutIcon />}
            text={<TypographyI18N translationKey={tk("navigation.logout")} />}
            onClick={onLogout}
            hoverColor={hoverColor}
          />
        </List>
        <Box
          sx={{
            display: "flex",
            flexGrow: 1,
            padding: "0.5rem 1rem",
          }}
        >
          <MHFormsLogo />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexGrow: 1,
            padding: "0.5rem 1rem",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.625rem",
              color: "#999",
            }}
          >
            v: {config.version}
          </Typography>
        </Box>
        {apiVersion && apiVersion !== config.version && (
          <Box
            sx={{
              display: "flex",
              flexGrow: 1,
              padding: "0 0 0.5rem 1rem",
              alignItems: "center",
            }}
          >
            <Warning
              sx={{
                color: "#999",
                fontSize: "0.625rem",
                marginRight: "0.25rem",
              }}
            />
            <Typography
              sx={{
                fontSize: "0.625rem",
                color: "#999",
              }}
            >
              api: {apiVersion}
            </Typography>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export const AppDrawer = (props: {
  user: ExtendedVisionUser;
  children: ReactNode;
}): ReactNode => {
  const { apiVersion } = useApiVersion();

  // Navigation functions
  const navigate = useNavigate();

  const location = useLocation();

  // Avoid navigating to the home page if the user is already there.
  // Navigating to the same URL can cause React Router to trigger a
  // same-URL navigation which resets component state and makes the
  // app appear unresponsive (DEV-1877).
  const onHome = useCallback(() => {
    const homePath = createRoute.home();
    if (location.pathname !== homePath) {
      navigate(homePath);
    }
  }, [navigate, location.pathname]);

  const onIncidentCreate = useCallback(() => {
    navigate(createRoute.incidentCreate());
  }, [navigate]);

  const onIncidentArchive = useCallback(() => {
    navigate(createRoute.incidentArchive());
  }, [navigate]);

  const onHelp = useCallback(() => {
    window.open(
      "https://thalamos1.zohodesk.eu/portal/en/kb",
      "_blank",
      "noopener,noreferrer",
    );
  }, []);

  const onPolicyHelp = useCallback(() => {
    window.open(
      "http://mpsweb.intranet.mps/policing/publicprotection/mental-health-policy/",
      "_blank",
      "noopener,noreferrer",
    );
  }, []);

  const onUserProfile = useCallback(() => {
    navigate(createRoute.userProfile(props.user.id));
  }, [navigate, props.user.id]);

  const onContextSelector = useCallback(() => {
    navigate(createRoute.contextSelector());
  }, [navigate]);

  const onLogout = useCallback(() => {
    window.location.href = `${config.backendUrl}/api/oauth/logout`; // TODO: Get from config.backendUrl,
  }, []);

  return (
    <AppDrawerInner
      {...props}
      apiVersion={apiVersion}
      onHome={onHome}
      onIncidentCreate={onIncidentCreate}
      onIncidentArchive={onIncidentArchive}
      onHelp={onHelp}
      onPolicyHelp={onPolicyHelp}
      onUserProfile={onUserProfile}
      onContextSelector={onContextSelector}
      onLogout={onLogout}
    />
  );
};

export type AppDrawerInnerProps = Prettify<
  ComponentProps<typeof AppDrawer> & DrawerItemsProps
>;

export const AppDrawerInner = (props: AppDrawerInnerProps): ReactNode => {
  const theme = useTheme();
  const lang = props.user.sessionOrganisationConfiguration?.language ?? "enBtp";

  const highlight = "rgba(255, 255, 255, 0.3)";
  const lowlight = "rgba(0, 0, 0, 0.5)";

  const drawerConfig: Record<
    AvailableLanguages,
    { color: string; hoverColor: string; logo: ReactNode }
  > = {
    enBtp: {
      color: theme.palette.common.btpBlue,
      hoverColor: lowlight,
      logo: <BTPLogo />,
    },
    enMet: {
      color: theme.palette.common.metBlue,
      hoverColor: lowlight,
      logo: <MetLogo />,
    },
    enColp: {
      color: theme.palette.common.black,
      hoverColor: highlight,
      logo: <ColpLogo />,
    },
    en: {
      color: theme.palette.primary.main,
      hoverColor: lowlight,
      logo: <ThalamosLogo />,
    },
  };
  const { color, hoverColor, logo } = drawerConfig[lang];
  const { children, ...remainingProps } = props;

  return (
    <Stack>
      <ThalamosAppDrawer
        appDrawerColor={color}
        hoverColor={hoverColor}
        logo={logo}
        drawerItems={
          <DrawerItems {...remainingProps} hoverColor={hoverColor} />
        }
      >
        {children}
      </ThalamosAppDrawer>
    </Stack>
  );
};
